import { IS_CHECKOUT_PAGE } from './constants';

const themeLayoutRoot = window.Shopify.routes?.root ?? '/';
const checkoutLocale =
  window.Shopify.Checkout?.locale?.toLowerCase() || window.Shopify.checkout?.customer_locale?.toLowerCase();

export const SHOP_ROOT = IS_CHECKOUT_PAGE
  ? // ? of note: if we plan to support multiple locales/language for US market we would want to update this logic to construct a US locale subfolder path, e.g. `/ko/` or `/ar/`
    !!checkoutLocale && checkoutLocale !== 'en-us'
    ? `/${checkoutLocale}/`
    : '/'
  : themeLayoutRoot;

const ALO_ORIGINS = [
  'https://aloyoga.com',
  'https://www.aloyoga.com',
  'https://uat.aloyoga.com',
  'https://qa.aloyoga.com',
  'https://dev.aloyoga.com',
  'https://alo-yoga.myshopify.com',
  'https://uataloyoga.myshopify.com',
  'https://dev2aloyoga.myshopify.com',
  'https://devaloyoga.myshopify.com',
];

export function currentPathEquals(path: string): boolean {
  return window.location.pathname === prependLocaleToPath(path);
}

// minor not a huge deal but technically the `prependLocaleToPath` can be subsumed by `prependLocaleToUrl`
export function prependLocaleToUrl(url: string): string {
  try {
    const urlObj = new URL(url);
    // Do not prepend locale if SHOP_ROOT is not set or if the origin is not from Alo
    if (!SHOP_ROOT || !ALO_ORIGINS.includes(urlObj.origin)) return url;
    if (SHOP_ROOT !== '/' && !urlObj.pathname.startsWith(removeTrailingSlash(SHOP_ROOT))) {
      const path = removeLeadingSlash(urlObj.pathname);
      urlObj.pathname = `${SHOP_ROOT}${path}`;
    }
    return urlObj.toString();
  } catch (e) {
    console.error(`Error in prependLocaleToUrl. url: ${url}`, e);
    return url;
  }
}

export function prependLocaleToPath(path: string): string {
  if (!SHOP_ROOT || SHOP_ROOT === '/') return path;
  if (!path.startsWith(removeTrailingSlash(SHOP_ROOT))) {
    // we're somewhat forced to ignore if the input path starts with `/` vs not since we can't form a valid localalized path w/o the leading `/`
    path = removeLeadingSlash(path);
    path = `${SHOP_ROOT}${path}`;
  }
  return path;
}

export function pathWithoutLocale(path: string): string {
  if (!SHOP_ROOT || SHOP_ROOT === '/') return path;
  if (path.startsWith(removeTrailingSlash(SHOP_ROOT))) {
    path = path.replace(removeTrailingSlash(SHOP_ROOT), '');
  }
  return path;
}

export function urlWithoutLocale(url: string): string {
  try {
    const urlObj = new URL(url);
    if (!SHOP_ROOT || !ALO_ORIGINS.includes(urlObj.origin)) return url;
    if (SHOP_ROOT !== '/' && urlObj.pathname.startsWith(removeTrailingSlash(SHOP_ROOT))) {
      urlObj.pathname = urlObj.pathname.replace(removeTrailingSlash(SHOP_ROOT), '');
    }
    return urlObj.toString();
  } catch (e) {
    console.error(`Error in urlWithoutLocale. url: ${url}`, e);
    return url;
  }
}

function removeLeadingSlash(str: string): string {
  if (str.startsWith('/')) {
    return str.substring(1);
  }
  return str;
}

function removeTrailingSlash(str: string): string {
  if (str.charAt(str.length - 1) === '/') {
    return str.slice(0, -1);
  }
  return str;
}

export const setupAutomaticLinkLocalization = () => {
  // early returns as needed; only run the automatic link localization as needed
  if (SHOP_ROOT === '/') return;

  // Prepend non "en" locales to aloyoga hrefs
  const updateLinksWithLocale = () => {
    const origin = window.location.origin;
    const links = document.querySelectorAll<HTMLAnchorElement>(
      `a[href^="/"]:not(a[href*="checkouts"], a[href*="policies"]), a[href^="${origin}"]:not(a[href*="checkouts"], a[href*="policies"])`
    );
    links.forEach((link) => {
      const localizedHref = prependLocaleToUrl(link.href);
      if (link.href !== localizedHref) {
        link.setAttribute('href', localizedHref);
      }
    });
  };

  // Prepend non "en" locales to aloyoga hrefs on page load
  document.addEventListener('DOMContentLoaded', updateLinksWithLocale);

  // Observe DOM changes to handle dynamically added links
  const mutationObserver = new MutationObserver(updateLinksWithLocale);

  // Start observing the document
  mutationObserver.observe(document.body, {
    childList: true,
    subtree: true,
  });
};
